import React, { useEffect, useMemo } from 'react';
import { IoSearchSharp } from 'react-icons/io5';
import { useIntl } from 'react-intl';
import { Card } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { selectRewashes, selectFromDate, selectUntilDate } from '../reducer';
import { selectCoreSiteId } from '../../settings/reducer';
import moment from 'moment';
import { getApiStatus } from '../../../core/utils';
import Loader from '../../../core/components/Loader';
import { fetchRewashes, setFromDate, setUntilDate } from '../actions';
import { map, has, join, keys, pickBy } from 'lodash';
import { FilterPlacement, PageFilters } from '../../../core/components/PageFilters';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import { useHistory } from 'react-router';
import VisualComponent from '../../overview/components/VisualComponent';
import { Col, Container, Row } from 'reactstrap';
import { selectDashboard } from '../../analytics/reducer';
import { rewashVisualIds } from '../chartTypes';
import UserGroupPermissions from '../../../core/components/UserGroupPermissions';
import { viewPermissionRefs } from '../../../core/components/UserGroupPermissions';

const toFilterDate = (value) => {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

const fromFilterDate = (date) => {
  if (!date) return '';
  return moment(date).format('YYYY-MM-DD');
};

const Rewashes = () => {
  const [searchText, setSearchText] = React.useState('');

  const rewashes = useSelector(selectRewashes);
  const rewashesLoading = useSelector((state) => getApiStatus(state, 'FETCH_REWASHES'));
  const fromDate = useSelector(selectFromDate);
  const untilDate = useSelector(selectUntilDate);
  const siteId = useSelector(selectCoreSiteId);
  const multiSite = useSelector(selectMultiSiteSelection);

  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  const getRewashes = () => {
    dispatch(
      fetchRewashes({
        types: 'Rewash',
        fromDate,
        untilDate,
        direction: 'desc',
      })
    );
  };

  const getRewashReason = (reclamation) => {
    if (!has(reclamation, 'incidentDetails.needsRewash')) {
      return intl.formatMessage({ id: 'noRewashReason' });
    }
    const rewashReasons = keys(pickBy(reclamation.incidentDetails.needsRewash));
    return join(
      map(rewashReasons, (reasonName) => {
        if (reasonName === '') {
          return intl.formatMessage({ id: 'other' });
        }
        return intl.formatMessage({ id: reasonName });
      }),
      ', '
    );
  };
  useEffect(() => {
    getRewashes();
  }, [fromDate, untilDate, siteId, multiSite]);

  const visualIds = [
    { id: 'd282e5ffa6e4dd290320', title: 'rewashByReason30Days' },
    { id: 'c2e191313cb6b64ea339', title: 'rewashByReasonTeam30Days' },
  ];

  const filteredRewashes = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();

    if (!normalizedSearchText) return rewashes;

    return rewashes.filter((rewash) =>
      [rewash.customerName, rewash.customerEmail, rewash.rewashOperation].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearchText)
      )
    );
  }, [rewashes, searchText]);

  const filterConfigs = useMemo(
    () => [
      {
        id: 'dateRange',
        type: 'dateRange',
        name: intl.formatMessage({ id: 'date' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <i className="uil uil-calendar-alt" />,
        startDate: toFilterDate(fromDate),
        endDate: toFilterDate(untilDate),
        native: true,
        onChange: ([startDate, endDate]) => {
          dispatch(setFromDate(startDate ? moment(fromFilterDate(startDate)) : ''));
          dispatch(setUntilDate(endDate ? moment(fromFilterDate(endDate)) : ''));
        },
      },
      {
        id: 'searchText',
        type: 'search',
        name: intl.formatMessage({ id: 'search', defaultMessage: 'Search' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <IoSearchSharp />,
        debounceMs: 0,
        searchTerm: searchText,
        onChange: (value) => setSearchText(value || ''),
      },
    ],
    [dispatch, fromDate, intl, searchText, untilDate]
  );

  return (
    <div className="container-fluid" data-testid="Rewashes">
      <section className="page-main-content">
        <UserGroupPermissions itemRef={viewPermissionRefs.rewash_chart_section}>
          <Container fluid className="overview px-4">
            <Row className="g-3">
              {rewashVisualIds.map(({ id, title }) => (
                <Col xs={12} md={4} lg={4} key={id}>
                  <VisualComponent
                    visualId={id}
                    pageName="ReportSectionde220715d8729a0eb06d"
                    title={title}
                    selectorFn={selectDashboard}
                    dashboardType="rewash"
                  />
                </Col>
              ))}
            </Row>
          </Container>
        </UserGroupPermissions>
        {rewashesLoading ? (
          <Loader />
        ) : (
          <div className="content-container">
            <div className="filters-actions-container mb-3">
              <PageFilters filters={filterConfigs} maxChicletFilters={2} />
            </div>

            <Col xs={12} lg={6}></Col>
            {rewashes.length ? (
              <div className="media-content">
                <div className="media media-rewash rewash-section">
                  {map(filteredRewashes, (rewash, i) => (
                    <Card
                      className="mouse-pointer"
                      key={i}
                      onClick={() => history.push(`rewashes/${rewash.id}/`)}
                    >
                      {rewash?.files && rewash?.files.length && rewash?.files[0]?.downloadUrl ? (
                        <div className="media-item no-border">
                          <img
                            src={rewash.files[0].downloadUrl}
                            alt="rewash vehicle image"
                            title="Rewash vehicle image"
                          />
                        </div>
                      ) : (
                        <div className="media-item">
                          <p className="media-item-description no-image">
                            <i
                              className="uil uil-image-times"
                              title={intl.formatMessage({ id: 'noImage' })}
                            ></i>
                          </p>
                        </div>
                      )}
                      <div className="details">
                        <p>
                          {intl.formatMessage({ id: 'reason' })}: {getRewashReason(rewash)}
                        </p>
                        <p data-testid="rewashDateIssued">
                          {intl.formatMessage({ id: 'dateIssued' })}:{' '}
                          {moment(rewash.issueDate).format('MM/DD/YYYY h:mm a')}
                        </p>
                        <p>
                          {intl.formatMessage({ id: 'operation' })}: {rewash.rewashOperation}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center">
                <h3>{intl.formatMessage({ id: 'noRewashes' })}</h3>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Rewashes;
